import React from "react"

import { Badge, Card, CardHeader, Divider } from "@coval/ui"

export interface TranscriptionErrorResult {
  wer: number
  divergent_sections_count: number
  reference_word_count: number
  hypothesis_word_count: number
  role?: "agent" | "persona" | "both"
}

interface TranscriptionErrorDetailsProps {
  result: TranscriptionErrorResult
}

function getScoreVariant(score: number): "success" | "warning" | "danger" {
  if (score < 0.1) {
    return "success"
  }
  if (score < 0.3) {
    return "warning"
  }
  return "danger"
}

export const TranscriptionErrorDetails: React.FC<
  TranscriptionErrorDetailsProps
> = ({ result }) => {
  const {
    divergent_sections_count,
    wer,
    reference_word_count,
    hypothesis_word_count,
  } = result

  return (
    <div className="gap-4 flex flex-col">
      <Card>
        <CardHeader className="flex flex-col gap-2 items-start">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-m text-coval-text-muted">
              Overall Word Error Rate:
            </p>
            <Badge variant={getScoreVariant(wer)} size="chip">
              WER: {(wer * 100).toFixed(2)}%
            </Badge>
            <Badge size="status">Reference Words: {reference_word_count}</Badge>
            <Badge size="status">
              Hypothesis Words: {hypothesis_word_count}
            </Badge>
          </div>
          <Divider />
          <p className="text-sm text-coval-text-muted">
            Found {divergent_sections_count} sections with transcription
            differences
          </p>
        </CardHeader>
      </Card>
      <div className="flex items-center gap-4 flex-wrap">
        <p className="text-sm text-coval-text-muted">Key:</p>
        <div className="flex items-center gap-1.5">
          <span className="bg-blue-300 dark:bg-blue-800 text-blue-900 dark:text-blue-100 px-1.5 py-0.5 underline rounded-full text-sm">
            inserted
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="bg-red-300 dark:bg-red-400 text-red-900 dark:text-red-900 line-through px-1.5 py-0.5 rounded-full text-sm">
            deleted
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="bg-yellow-300 dark:bg-yellow-400 text-amber-950 px-1.5 py-0.5 rounded-full text-sm">
            substitute
          </span>
        </div>
      </div>
    </div>
  )
}
